import React, { useEffect, useState } from 'react'
import * as moment from 'moment'
import { observer } from 'mobx-react'
import { observe } from 'mobx'
import { AppState } from '../../stores/app'
import { AppService } from '../../services/index'
import { PlatformMessageDto } from '../../dtos/platform-message'
import { PlatformMessageItem } from './platform-message-item'
import { APPLICATION_VERSION } from '../../constants'
import { useService } from '@pushly/aqe/lib/hooks'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import './platform-message-bar.scss'
import { arrayContains } from '../../_utils/utils'

export const PlatformMessageBar: React.FC = observer(() => {
    const appState = useService(AppState)
    const appService = useService(AppService)
    const userPreferences = useUserPreferences(appState.currentUser?.id)

    const [messages, setMessages] = useState<PlatformMessageDto[]>([])

    const getCurrentPlatformMessages = (): PlatformMessageDto[] => {
        const user = appState.currentUser
        const seenMsgs = userPreferences.get(['ackd_platform_messages'])
        let platformMsgs = appState.platformMessages || []

        if (user) {
            // Filter already seen messages out
            platformMsgs = platformMsgs.filter((msg) => !arrayContains(seenMsgs, msg.id))
            // Filter remaining by domain visibility
            platformMsgs = platformMsgs.filter(
                (msg) => !msg.domainIds || arrayContains(msg.domainIds, appState.currentDomain!.id),
            )
        }

        return platformMsgs
    }

    const updatePlatformMessages = () => {
        setMessages(getCurrentPlatformMessages())
    }

    useEffect(() => {
        updatePlatformMessages()

        const disposers = [
            observe(appState, 'currentDomainJsonData', () => updatePlatformMessages()),
            observe(appState, 'platformMessages', () => updatePlatformMessages()),
            observe(appState, 'userPlatformMessages', () => updatePlatformMessages()),
        ]

        return () => {
            disposers.forEach((dispose) => dispose())
        }
    }, [appState, appService])

    const hasMessages = messages && messages.length > 0

    const classNames: string[] = ['platform-message-bar']
    const shadowClassNames: string[] = ['platform-message-bar-shadow']
    if (hasMessages) {
        classNames.push('has-messages')
        shadowClassNames.push('has-messages')
    }

    const standardMessages = messages.filter((msg) => !msg.minVersionReq && !msg.maintenanceMode)
    const maintenanceMessages = messages.filter(
        (msg) => !!msg.maintenanceMode && (!msg.endTime || moment(msg.endTime) > moment()),
    )
    const refreshMessages = messages.filter((msg) => !!msg.minVersionReq && msg.minVersionReq > APPLICATION_VERSION)

    if (!!maintenanceMessages && maintenanceMessages.length > 0) {
        // SORT DESC BY minVersionReq
        maintenanceMessages.sort((msgA, msgB) => {
            return msgA.startTime! < msgB.startTime! ? 1 : msgA.startTime! > msgB.startTime! ? -1 : 0
        })

        const maintenanceMessage = maintenanceMessages[0]

        return (
            <div className="platform-maintenance-message">
                <PlatformMessageItem
                    key={maintenanceMessage.id}
                    {...maintenanceMessage}
                    isMaintenanceMode={true}
                    messageId={maintenanceMessage.id}
                    userId={appState.currentUser?.id}
                    userPreferences={userPreferences}
                />
            </div>
        )
    }

    if (!!refreshMessages && refreshMessages.length > 0) {
        // SORT DESC BY minVersionReq
        refreshMessages.sort((msgA, msgB) => {
            return msgA.minVersionReq! < msgB.minVersionReq! ? 1 : msgA.minVersionReq! > msgB.minVersionReq! ? -1 : 0
        })

        const refreshMessage = refreshMessages[0]

        return (
            <div className="platform-refresh-message">
                <PlatformMessageItem
                    key={refreshMessage.id}
                    {...refreshMessage}
                    isRefreshMode={true}
                    messageId={refreshMessage.id}
                    userId={appState.currentUser?.id}
                    userPreferences={userPreferences}
                />
            </div>
        )
    }

    const msgComponents = standardMessages.map((msg) => (
        <PlatformMessageItem
            key={msg.id}
            {...msg}
            messageId={msg.id}
            userId={appState.currentUser?.id}
            userPreferences={userPreferences}
        />
    ))

    return (
        <React.Fragment>
            <div className={classNames.join(' ')}>{msgComponents}</div>
            <div className={shadowClassNames.join(' ')}>{msgComponents}</div>
        </React.Fragment>
    )
})
